/* eslint-disable @typescript-eslint/consistent-type-imports */
import type { ReactNode } from 'react';
import React, { useMemo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { ActivationTrigger } from '../types.ts';
import { ActivationReason } from '../types.ts';

import { makeGate } from './Gate.tsx';
import { Boundary } from './Boundary.tsx';
import { useActivationTriggers } from './useActivationTriggers.ts';

type ActivatableComponentProps = {
	children: ReactNode;
	triggers?: ActivationTrigger[];
	onActivate?: (reason: ActivationReason) => void;
	id: string;
	analyticsName?: string;
	ssrSuccess?: boolean;
};

export function ActivatableComponent({
	children,
	triggers,
	onActivate,
	id,
	analyticsName,
	ssrSuccess,
}: ActivatableComponentProps): React.JSX.Element {
	const gate = useMemo(() => makeGate(), []);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const renderChildren = useCallback(() => {
		return children;
	}, [children]);

	const activateComponent = useCallback(
		(reason: ActivationReason) => {
			if (gate?.getStatus() === 'suspended') {
				onActivate?.(reason);

				if (analyticsName) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'activatableComponent',
							action: 'activated',
							actionSubject: 'activatableComponent',
							attributes: {
								analyticsName,
								triggers,
								boundaryId: id,
								activationReason: reason,
							},
						},
					}).fire();
				}

				gate.resolve();
			}
		},
		[gate, id, onActivate, createAnalyticsEvent, analyticsName, triggers],
	);

	const hostRefWithActivation = useActivationTriggers({
		triggers,
		gate,
		activateComponent,
		boundaryId: id,
		analyticsName,
	});

	return (
		<div ref={hostRefWithActivation} data-hydration-shield={id}>
			<Boundary
				gate={gate}
				id={id}
				render={renderChildren}
				ssrSuccess={ssrSuccess}
				activateComponent={activateComponent}
				resolved={gate?.getStatus?.() === 'resolved'}
				boundaryName={analyticsName || id}
				triggers={triggers}
			/>
		</div>
	);
}
